import { Mail, RefreshCw, Trash2, Edit, CheckCircle2, XCircle, Power, AlertCircle, RotateCcw, Zap, Square, KeyRound } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Account, SyncProgress } from '../../types';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useProviders } from '../../hooks/useProviders';

interface AccountCardProps {
  account: Account;
  onSync?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onToggleStatus?: () => void;
  onClearError?: () => void;
  onRestore?: () => void;
  onForceDelete?: () => void;
  onCancelSync?: () => void;
  onReauthorize?: () => void; // 重新授权回调
  isSyncing?: boolean;
  syncProgress?: SyncProgress;
  // 新增属性
  density?: 'detailed' | 'compact' | 'minimal';
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  showSelection?: boolean;
}

export const AccountCard = ({
  account,
  onSync,
  onDelete,
  onEdit,
  onToggleStatus,
  onClearError,
  onRestore,
  onForceDelete,
  onCancelSync,
  onReauthorize,
  isSyncing,
  syncProgress,
  density = 'detailed',
  isSelected = false,
  onSelect,
  showSelection = false,
}: AccountCardProps) => {
  const { getProviderByName } = useProviders();

  // 检测是否需要重新授权（OAuth2 token 过期）
  const needsReauthorization = () => {
    if (!account.last_sync_error) return false;
    const error = account.last_sync_error.toLowerCase();
    // 检测常见的 OAuth2 token 过期错误
    return (
      error.includes('invalid_grant') ||
      error.includes('token has been expired') ||
      error.includes('token expired') ||
      error.includes('refresh token') ||
      error.includes('oauth2') ||
      error.includes('unauthorized') ||
      error.includes('401')
    );
  };

  // 检测是否是 OAuth2 账户
  const isOAuth2Account = () => {
    return account.auth_type === 'oauth2' || 
           account.provider === 'gmail' || 
           account.provider === 'outlook';
  };

  // 计算同步进度百分比
  const getSyncProgressPercent = () => {
    if (!syncProgress || syncProgress.total_estimated === 0) return 0;
    return Math.min(100, Math.round((syncProgress.processed / syncProgress.total_estimated) * 100));
  };

  // 获取同步阶段显示文本
  const getSyncPhaseText = () => {
    if (!syncProgress) return '';
    switch (syncProgress.phase) {
      case 'fetching': return '拉取邮件中';
      case 'processing': return '处理邮件中';
      case 'finalizing': return '完成中';
      default: return '同步中';
    }
  };

  // 渲染同步按钮（支持取消）
  const renderSyncButton = (size: 'icon' | 'sm' = 'icon') => {
    if (isSyncing || syncProgress) {
      return (
        <Button
          variant="ghost"
          size={size}
          onClick={onCancelSync}
          title="取消同步"
          className="text-orange-600 hover:text-orange-700"
        >
          <Square className={size === 'icon' ? 'h-4 w-4' : 'h-3 w-3'} />
        </Button>
      );
    }
    return (
      <Button
        variant="ghost"
        size={size}
        onClick={onSync}
        disabled={isSyncing}
        title="同步"
      >
        <RefreshCw className={`${size === 'icon' ? 'h-4 w-4' : 'h-3 w-3'} ${isSyncing ? 'animate-spin' : ''}`} />
      </Button>
    );
  };

  // 渲染同步进度条
  const renderSyncProgress = () => {
    if (!syncProgress || syncProgress.status === 'completed' || syncProgress.status === 'failed' || syncProgress.status === 'cancelled') {
      return null;
    }
    
    const percent = getSyncProgressPercent();
    const phaseText = getSyncPhaseText();
    
    return (
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {phaseText}
          </span>
          <span>
            {syncProgress.processed}/{syncProgress.total_estimated} ({percent}%)
          </span>
        </div>
        <Progress value={percent} className="h-1.5" />
        {syncProgress.new_emails > 0 && (
          <div className="text-xs text-muted-foreground">
            新增 {syncProgress.new_emails} 封
            {syncProgress.updated_emails > 0 && `，更新 ${syncProgress.updated_emails} 封`}
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '从未同步';
    try {
      return format(new Date(dateString), 'PPP HH:mm', { locale: zhCN });
    } catch {
      return dateString;
    }
  };

  const getProviderName = (provider: string) => {
    const providerInfo = getProviderByName(provider);
    return providerInfo?.display_name || provider;
  };

  const getSyncStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'running':
        return 'text-blue-600';
      default:
        return 'text-muted-foreground';
    }
  };

  // 根据密度渲染不同的布局
  // 回收站：有 deleted_at，或父组件显式传入恢复/永久删除回调
  const isDeletedAccount = !!account.deleted_at || (!!onRestore && !!onForceDelete);

  if (density === 'minimal') {
    const isDeleted = isDeletedAccount;

    return (
      <Card
        className={`hover:shadow-sm transition-all duration-200 ${isSelected ? 'ring-2 ring-primary' : ''} ${isDeleted ? 'opacity-60' : ''}`}
        data-testid="account-card"
      >
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {showSelection && !isDeleted && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={onSelect}
                />
              )}
              <div className="flex items-center gap-2">
                {isDeleted ? (
                  <Trash2 className="h-4 w-4 text-gray-400" />
                ) : account.status === 'active' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : account.status === 'disabled' && account.disable_reason === 'auto_disabled_auth_failure' ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : account.status === 'disabled' ? (
                  <XCircle className="h-4 w-4 text-red-600" />
                ) : account.status === 'error' ? (
                  <XCircle className="h-4 w-4 text-orange-600" />
                ) : null}
              </div>
              <span className={`font-medium truncate ${isDeleted ? 'text-gray-500' : ''}`}>
                {account.email}
              </span>
              <Badge variant="outline" className="text-xs">
                {getProviderName(account.provider)}
              </Badge>
              {isDeleted && (
                <Badge variant="secondary" className="text-xs">
                  已删除
                </Badge>
              )}
              {!isDeleted && account.disable_reason === 'auto_disabled_auth_failure' && (
                <Badge variant="destructive" className="text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  已自动禁用
                </Badge>
              )}
              {!isDeleted && account.auth_type === 'quick' && account.consecutive_auth_failures > 0 && account.status !== 'disabled' && (
                <Badge variant="destructive" className="text-xs">
                  {account.consecutive_auth_failures}/3
                </Badge>
              )}
              {!isDeleted && account.last_sync_error && !account.disable_reason && (
                <Badge variant="destructive" className="text-xs">
                  错误
                </Badge>
              )}
              {!isDeleted && needsReauthorization() && isOAuth2Account() && (
                <Badge variant="destructive" className="text-xs flex items-center gap-1">
                  <KeyRound className="h-3 w-3" />
                  需重新授权
                </Badge>
              )}
            </div>
            <div className="flex gap-1">
              {isDeleted ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRestore}
                    title="恢复"
                    className="text-blue-600"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onForceDelete}
                    title="永久删除"
                    className="text-red-600"
                  >
                    <Zap className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <>
                  {needsReauthorization() && isOAuth2Account() && onReauthorize && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onReauthorize}
                      title="重新授权"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <KeyRound className="h-3 w-3" />
                    </Button>
                  )}
                  {renderSyncButton('sm')}
                  <Button variant="ghost" size="sm" onClick={onEdit} title="编辑">
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleStatus}
                    title={account.status === 'active' ? '禁用' : '启用'}
                    className={account.status === 'active' ? 'text-orange-600' : 'text-green-600'}
                  >
                    <Power className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
          {/* 同步进度（minimal 视图） */}
          {renderSyncProgress()}
        </CardContent>
      </Card>
    );
  }

  if (density === 'compact') {
    const isDeleted = isDeletedAccount;

    return (
      <Card
        className={`hover:shadow-md transition-all duration-200 ${isSelected ? 'ring-2 ring-primary' : ''} ${isDeleted ? 'border border-red-300 bg-red-50' : ''}`}
        data-testid="account-card"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {showSelection && !isDeleted && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={onSelect}
                />
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Mail className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-medium truncate ${isDeleted ? 'text-red-600' : ''}`}>
                    {account.email}
                  </span>
                  {isDeleted ? (
                    <Badge variant="destructive" className="text-xs">
                      已删除
                    </Badge>
                  ) : (
                    <>
                      {account.status === 'active' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : account.status === 'disabled' && account.disable_reason === 'auto_disabled_auth_failure' ? (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      ) : account.status === 'disabled' ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : account.status === 'error' ? (
                        <XCircle className="h-4 w-4 text-orange-600" />
                      ) : null}
                      {account.disable_reason === 'auto_disabled_auth_failure' && (
                        <Badge variant="destructive" className="text-xs">
                          已自动禁用
                        </Badge>
                      )}
                      {account.auth_type === 'quick' && account.consecutive_auth_failures > 0 && account.status !== 'disabled' && (
                        <Badge variant="destructive" className="text-xs">
                          {account.consecutive_auth_failures}/3
                        </Badge>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{getProviderName(account.provider)}</span>
                  <span>•</span>
                  <span className={getSyncStatusColor(account.last_sync_status)}>
                    {account.last_sync_status === 'success'
                      ? '同步成功'
                      : account.last_sync_status === 'failed'
                      ? '同步失败'
                      : account.last_sync_status === 'running'
                      ? '同步中'
                      : '未同步'}
                  </span>
                  {account.last_sync_at && (
                    <>
                      <span>•</span>
                      <span>{formatDate(account.last_sync_at)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              {isDeleted ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onRestore}
                    title="恢复账号"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onForceDelete}
                    title="永久删除"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  {needsReauthorization() && isOAuth2Account() && onReauthorize && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onReauthorize}
                      title="重新授权"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  )}
                  {renderSyncButton('icon')}
                  <Button variant="ghost" size="icon" onClick={onEdit} title="编辑账户">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleStatus}
                    title={account.status === 'active' ? '禁用账户' : '启用账户'}
                    className={account.status === 'active' ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onDelete}
                    title="删除"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* 同步进度（compact 视图） */}
          {renderSyncProgress()}

          {/* 错误信息 */}
          {account.last_sync_error && !syncProgress && (
            <div className="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-600 dark:bg-red-950/20">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 text-xs">
                  {account.last_sync_error}
                </div>
                <div className="flex gap-1">
                  {needsReauthorization() && isOAuth2Account() && onReauthorize && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onReauthorize}
                      className="h-5 px-1 text-xs text-blue-600 hover:text-blue-700"
                      title="重新授权"
                    >
                      重新授权
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onClearError?.()}
                    className="h-5 px-1 text-xs text-red-600 hover:text-red-700"
                    title="清除错误状态"
                  >
                    清除
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const isDeleted = isDeletedAccount;

  // 详细视图（原有的完整布局）
  return (
    <Card
      className={`transition-all duration-200 ${isSelected ? 'ring-2 ring-primary' : ''} ${isDeleted ? 'border border-red-300 bg-red-50' : ''}`}
      data-testid="account-card"
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {showSelection && !isDeleted && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelect}
              />
            )}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Mail className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-lg">{account.email}</CardTitle>
              <CardDescription>{getProviderName(account.provider)}</CardDescription>
            </div>
          </div>
          <div className="flex gap-1">
            {isDeleted ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onRestore}
                  title="恢复账号"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onForceDelete}
                  title="永久删除"
                  className="text-red-600 hover:text-red-700"
                >
                  <Zap className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                {needsReauthorization() && isOAuth2Account() && onReauthorize && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onReauthorize}
                    title="重新授权"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                )}
                {renderSyncButton('icon')}
                <Button variant="ghost" size="icon" onClick={onEdit} title="编辑账户">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleStatus}
                  title={account.status === 'active' ? '禁用账户' : '启用账户'}
                  className={account.status === 'active' ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                  data-testid="toggle-status-button"
                >
                  <Power className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onDelete}
                  title="删除"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {/* 账户状态 */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">账户状态</span>
            <div className="flex items-center gap-2">
              {account.status === 'active' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" data-testid="account-status-icon" />
              ) : account.status === 'disabled' && account.disable_reason === 'auto_disabled_auth_failure' ? (
                <AlertCircle className="h-4 w-4 text-red-600" data-testid="account-status-icon" />
              ) : account.status === 'disabled' ? (
                <XCircle className="h-4 w-4 text-red-600" data-testid="account-status-icon" />
              ) : account.status === 'error' ? (
                <XCircle className="h-4 w-4 text-orange-600" data-testid="account-status-icon" />
              ) : null}
              <Badge 
                variant={account.status === 'active' ? 'default' : 'destructive'} 
                data-testid="account-status-badge"
                className={account.disable_reason === 'auto_disabled_auth_failure' ? 'bg-red-600' : ''}
              >
                {account.status === 'active'
                  ? '正常'
                  : account.status === 'disabled' && account.disable_reason === 'auto_disabled_auth_failure'
                  ? '已自动禁用'
                  : account.status === 'disabled'
                  ? '已禁用'
                  : account.status === 'error'
                  ? '错误'
                  : '未知'}
              </Badge>
            </div>
          </div>

          {/* 失败计数（仅对 quick 账号显示） */}
          {account.auth_type === 'quick' && account.consecutive_auth_failures > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">认证失败次数</span>
              <Badge variant="destructive" className="text-xs">
                {account.consecutive_auth_failures}/3
              </Badge>
            </div>
          )}

          {/* 自动禁用时间 */}
          {account.auto_disabled_at && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">禁用时间</span>
              <span className="text-red-600">{formatDate(account.auto_disabled_at)}</span>
            </div>
          )}

          {/* 同步状态 */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">同步状态</span>
            <div className="flex items-center gap-2">
              {account.last_sync_status === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : account.last_sync_status === 'failed' ? (
                <XCircle className="h-4 w-4 text-red-600" />
              ) : null}
              <span className={getSyncStatusColor(account.last_sync_status)}>
                {account.last_sync_status === 'success'
                  ? '成功'
                  : account.last_sync_status === 'failed'
                  ? '失败'
                  : account.last_sync_status === 'running'
                  ? '同步中'
                  : '未同步'}
              </span>
            </div>
          </div>

          {/* 最后同步时间 */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">最后同步</span>
            <span>{formatDate(account.last_sync_at)}</span>
          </div>

          {/* 同步设置 */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">自动同步</span>
            <div className="flex items-center gap-2">
              <Badge variant={account.sync_enabled ? 'default' : 'secondary'}>
                {account.sync_enabled ? '已启用' : '已禁用'}
              </Badge>
              {account.sync_enabled && (
                <span className="text-muted-foreground">
                  每 {account.sync_interval} 分钟
                </span>
              )}
            </div>
          </div>

          {/* 同步进度（detailed 视图） */}
          {renderSyncProgress()}

          {/* 错误信息 */}
          {account.last_sync_error && !syncProgress && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950/20">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  {account.last_sync_error}
                </div>
                <div className="flex gap-1">
                  {needsReauthorization() && isOAuth2Account() && onReauthorize && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={onReauthorize}
                      className="h-6 px-2 text-xs"
                      title="重新授权"
                    >
                      <KeyRound className="h-3 w-3 mr-1" />
                      重新授权
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onClearError?.()}
                    className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                    title="清除错误状态"
                  >
                    清除
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};